/**
 * CrumblePlatformGenerator: in Unity the generator's editor script spawns one `crumblePrefab` tile per unit of its
 * sprite width under the platform. Those tiles are derived data here: documents only keep the generator, the
 * editor draws its tiled sprite, and play mode / the Unity importer generate the tiles.
 */
import type { LevelDoc, PlacedObject, PropMod } from '../model/format.ts';
import { apply, compose } from '../model/xform.ts';
import { arr, num } from '../model/unityProps.ts';
import { gameData } from './gameData.ts';
import { instantiatePrefab, type PrefabInstance } from './prefabInstance.ts';

export const CRUMBLE_GENERATOR = 'CrumblePlatformGenerator';

interface GeneratorInfo {
  inst: PrefabInstance;
  tilePrefab: string | null;
  width: number;
  roundLeft: boolean;
  roundRight: boolean;
  sprite: any;
}

const infoCache = new WeakMap<PlacedObject, GeneratorInfo | null>();

/** The generator settings of a placed object, or null when it isn't a crumble platform generator. */
export function crumbleGenerator(o: PlacedObject): GeneratorInfo | null {
  if (!o.prefab) return null;
  const hit = infoCache.get(o);
  if (hit !== undefined) return hit;
  let info: GeneratorInfo | null = null;
  const def = gameData().prefabs[o.prefab];
  if (def && Object.values(def.nodes).some((n) => n.type === CRUMBLE_GENERATOR)) {
    const inst = instantiatePrefab(o.prefab, o.mods);
    const gen = inst?.root.comps.find((c) => c.type === CRUMBLE_GENERATOR)?.data;
    const sr = inst?.root.comps.find((c) => c.type === 'SpriteRenderer')?.data;
    if (inst && gen) {
      info = {
        inst,
        tilePrefab: gen.crumblePrefab?.guid ? String(gen.crumblePrefab.guid) : null,
        width: num(sr?.m_Size?.x, 1),
        roundLeft: String(gen.roundLeftSide ?? '0') === '1',
        roundRight: String(gen.roundRightSide ?? '0') === '1',
        sprite: sr,
      };
    }
  }
  infoCache.set(o, info);
  return info;
}

/** Prefab children of a generator instance that are its baked tiles (replaced by generated ones). */
export function isPrefabTile(info: GeneratorInfo, goName: string): boolean {
  return /^Crumble_\d+$/.test(goName) && info.inst.gos.some((g) => g.parent === info.inst.root && g.name === goName);
}

/** Tiles generated in Unity and stored as objects attached to their generator. */
export function isGeneratedTile(doc: LevelDoc, o: PlacedObject, generators = generatorIds(doc)): boolean {
  if (!o.attach || o.attach.path || !generators.has(o.attach.object)) return false;
  return o.prefab === generators.get(o.attach.object) || /^Crumble_\d+$/.test(o.name);
}

/** generator object id -> tile prefab guid */
function generatorIds(doc: LevelDoc): Map<string, string | null> {
  const out = new Map<string, string | null>();
  for (const o of [...doc.objects, ...doc.bodies.flatMap((b) => b.objects)]) {
    const g = crumbleGenerator(o);
    if (g) out.set(o.id, g.tilePrefab);
  }
  return out;
}

/** The document without stored generated tiles (unchanged when there are none). */
export function stripGeneratedTiles(doc: LevelDoc): LevelDoc {
  const gens = generatorIds(doc);
  if (!gens.size) return doc;
  const keep = (list: PlacedObject[]) => {
    const out = list.filter((o) => !isGeneratedTile(doc, o, gens));
    return out.length === list.length ? list : out;
  };
  const objects = keep(doc.objects);
  let bodiesChanged = false;
  const bodies = doc.bodies.map((b) => {
    const objs = keep(b.objects);
    if (objs === b.objects) return b;
    bodiesChanged = true;
    return { ...b, objects: objs };
  });
  if (objects === doc.objects && !bodiesChanged) return doc;
  return { ...doc, objects, bodies: bodiesChanged ? bodies : doc.bodies };
}

/** CrumblePlatform.SpriteType */
const enum SpriteType { Left = 0, Middle = 1, Right = 2, Single = 3 }
const ANIMATION_FIELD = ['leftAnimation', 'middleAnimation', 'rightAnimation', 'singleAnimation'];

/** GeneratePlatforms: one tile per unit of width, rounded at the ends that ask for it. */
export function generateCrumbleTiles(o: PlacedObject): PlacedObject[] {
  const info = crumbleGenerator(o);
  if (!info?.tilePrefab) return [];
  const gd = gameData();
  const tileDef = gd.prefabs[info.tilePrefab];
  if (!tileDef) return [];
  const rootComps = tileDef.gos[0]?.comps ?? [];
  const nodeOf = (type: string) => rootComps.find((id) => tileDef.nodes[id]?.type === type);
  const crumbleNode = nodeOf('CrumblePlatform'), srNode = nodeOf('SpriteRenderer'), animNode = nodeOf('Animator');
  const crumbleData = crumbleNode ? tileDef.nodes[crumbleNode].data : null;

  const count = Math.max(0, Math.round(info.width));
  const start = -count / 2;
  const world = compose(o.x, o.y, o.rotation, o.scale[0], o.scale[1]);
  const mat = arr(info.sprite?.m_Materials)[0];
  const material = mat?.guid ? `asset:${mat.guid}:${mat.fileID}` : null;
  const color = info.sprite?.m_Color;
  const tiles: PlacedObject[] = [];
  for (let i = 0; i < count; i++) {
    let type: SpriteType;
    if (info.width < 2) {
      type = info.roundLeft && info.roundRight ? SpriteType.Single : info.roundLeft ? SpriteType.Left : info.roundRight ? SpriteType.Right : SpriteType.Middle;
    } else if (i === 0 && info.roundLeft) type = SpriteType.Left;
    else if (i === count - 1 && info.roundRight) type = SpriteType.Right;
    else type = SpriteType.Middle;

    const mods: PropMod[] = [];
    if (crumbleNode) mods.push({ t: crumbleNode, p: 'spriteType', v: String(type) });
    const controller = crumbleData?.[ANIMATION_FIELD[type]];
    if (animNode && controller?.guid) mods.push({ t: animNode, p: 'm_Controller', r: `asset:${controller.guid}:${controller.fileID}` });
    const first = controller?.guid ? firstSprite(String(controller.guid)) : null;
    if (srNode && first) mods.push({ t: srNode, p: 'm_Sprite', r: `asset:${first}` });
    if (srNode && material) mods.push({ t: srNode, p: 'm_Materials.Array.data[0]', r: material });
    if (srNode && color) for (const ch of ['r', 'g', 'b', 'a'] as const) if (color[ch] !== undefined) mods.push({ t: srNode, p: `m_Color.${ch}`, v: String(color[ch]) });

    const [x, y] = apply(world, start + i + 0.5, 0);
    tiles.push({
      id: `${o.id}~crumble${i}`,
      name: `Crumble_${i}`,
      prefab: info.tilePrefab,
      x: +x.toFixed(4),
      y: +y.toFixed(4),
      rotation: o.rotation,
      scale: [o.scale[0], o.scale[1]],
      mods,
      attach: { object: o.id, path: '' },
    });
  }
  return tiles;
}

/** "<texture guid>:<sprite id>" of the first frame of a controller's default state. */
function firstSprite(controller: string): string | null {
  const anims = gameData().animations;
  const ctrl = anims?.controllers[controller];
  const clip = ctrl ? anims?.clips[ctrl.states[ctrl.defaultState]?.clip ?? ''] : null;
  return clip?.keys[0]?.[1] ?? null;
}

/** The document with every generator's tiles generated (play mode). */
export function withGeneratedTiles(doc: LevelDoc): LevelDoc {
  const base = stripGeneratedTiles(doc);
  const expand = (list: PlacedObject[]) => {
    const extra = list.flatMap((o) => generateCrumbleTiles(o));
    return extra.length ? [...list, ...extra] : list;
  };
  const objects = expand(base.objects);
  const bodies = base.bodies.map((b) => {
    const objs = expand(b.objects);
    return objs === b.objects ? b : { ...b, objects: objs };
  });
  return { ...base, objects, bodies };
}
